import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiCheck, FiLayout, FiImage, FiSettings, FiPlus, FiTrash2, FiEdit2, FiUpload, FiStar } from 'react-icons/fi';
import ColorPickerMock from './ColorPickerMock';
import supabase from '../../supabase/supabase';
import { compressImage } from '../../utils/imageCompression';

const defaultReviews = [
  { id: 'default-1', name: 'Sarah Jenkins', role: 'Verified Buyer', content: 'Absolutely love this product! It exceeded all my expectations and the quality is outstanding.', rating: 5, avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=128&q=80', order: 0 },
  { id: 'default-2', name: 'Michael Chen', role: 'Entrepreneur', content: 'This completely transformed how I run my business. Highly recommended to anyone on the fence.', rating: 5, avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=128&q=80', order: 1 },
  { id: 'default-3', name: 'Emily Rodriguez', role: 'Designer', content: 'Incredible attention to detail and superb customer service. I will definitely be a returning customer.', rating: 4, avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=128&q=80', order: 2 }
];

const ReviewsWidgetPopup = ({ currentStyles = {}, onUpdate, onClose, projectId }) => {
  const [styles, setStyles] = useState({
    columns: 3,
    showAvatars: true,
    showSummary: true,
    cardBgColor: '#ffffff',
    textColor: '#111827',
    starColor: '#f59e0b',
    borderRadius: 16,
    ...currentStyles
  });

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(null);

  useEffect(() => {
    loadReviews();
  }, [projectId]);

  const loadReviews = async () => {
    if (!projectId) {
      setReviews(defaultReviews);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('reviews_1775200000000')
        .select('*')
        .eq('project_id', projectId)
        .order('order', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setReviews(data);
      } else {
        // Insert default reviews for new projects
        const defaultsWithProjectId = defaultReviews.map((r, idx) => ({
          project_id: projectId,
          name: r.name,
          role: r.role,
          content: r.content,
          rating: r.rating,
          avatar_url: r.avatar_url,
          order: idx
        }));

        const { data: inserted, error: insertError } = await supabase
          .from('reviews_1775200000000')
          .insert(defaultsWithProjectId)
          .select();

        if (insertError) throw insertError;
        setReviews(inserted || defaultReviews);
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
      setReviews(defaultReviews);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setStyles(prev => ({ ...prev, [key]: value }));
  };

  const handleAddReview = async () => {
    const newReview = {
      project_id: projectId,
      name: 'New Reviewer',
      role: 'Customer',
      content: 'Write your review here...',
      rating: 5,
      avatar_url: '',
      order: reviews.length
    };

    if (projectId) {
      try {
        const { data, error } = await supabase
          .from('reviews_1775200000000')
          .insert([newReview])
          .select()
          .single();

        if (error) throw error;
        setReviews(prev => [...prev, data]);
        setEditingReview(data);
      } catch (err) {
        console.error('Error adding review:', err);
        alert('Failed to add review. Please try again.');
      }
    } else {
      const tempReview = { ...newReview, id: `temp-${Date.now()}` };
      setReviews(prev => [...prev, tempReview]);
      setEditingReview(tempReview);
    }
  };

  const handleUpdateReview = async (reviewId, updates) => {
    if (projectId && !reviewId.toString().startsWith('temp-') && !reviewId.toString().startsWith('default-')) {
      try {
        const { error } = await supabase
          .from('reviews_1775200000000')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', reviewId);

        if (error) throw error;
      } catch (err) {
        console.error('Error updating review:', err);
        alert('Failed to update review. Please try again.');
        return;
      }
    }

    // Use functional update to avoid stale closures
    setReviews(prevReviews => 
      prevReviews.map(r => r.id === reviewId ? { ...r, ...updates } : r)
    );
  };

  const handleDeleteReview = async (reviewId) => {
    if (projectId && !reviewId.toString().startsWith('temp-') && !reviewId.toString().startsWith('default-')) {
      try {
        // Delete avatar from storage if exists
        const review = reviews.find(r => r.id === reviewId);
        if (review?.avatar_url && review.avatar_url.includes('reviews_avatars')) {
          const fileName = review.avatar_url.split('/reviews_avatars/')[1];
          if (fileName) {
            await supabase.storage.from('reviews_avatars').remove([fileName]);
          }
        }

        const { error } = await supabase
          .from('reviews_1775200000000')
          .delete()
          .eq('id', reviewId);

        if (error) throw error;
      } catch (err) {
        console.error('Error deleting review:', err);
        alert('Failed to delete review. Please try again.');
      }
    }

    setReviews(prev => prev.filter(r => r.id !== reviewId));
    if (editingReview?.id === reviewId) setEditingReview(null);
  };

  const handleAvatarUpload = async (reviewId, file) => {
    console.log('Avatar upload started', { reviewId, fileName: file?.name, projectId });

    if (!file) {
      console.error('No file provided');
      return;
    }

    setUploadingAvatar(reviewId);

    try {
      // Compress the image with correct parameters
      const compressed = await compressImage(file, { 
        maxWidth: 200,
        maxHeight: 200,
        quality: 0.8
      });
      
      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const folderName = projectId || 'temp';
      const fileName = `${folderName}/${reviewId}-${Date.now()}.${fileExt}`;

      // Upload to the reviews_avatars bucket
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('reviews_avatars')
        .upload(fileName, compressed, { 
          upsert: true,
          contentType: compressed.type 
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('reviews_avatars')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      const newAvatarUrl = urlData.publicUrl;

      // Update the database if it's a saved project
      if (projectId && !reviewId.toString().startsWith('temp-') && !reviewId.toString().startsWith('default-')) {
        const { error } = await supabase
          .from('reviews_1775200000000')
          .update({ avatar_url: newAvatarUrl, updated_at: new Date().toISOString() })
          .eq('id', reviewId);

        if (error) console.error('Error updating review avatar in db:', error);
      }

      // Update local state directly using functional update to avoid stale closures
      setReviews(prevReviews => 
        prevReviews.map(r => r.id === reviewId ? { ...r, avatar_url: newAvatarUrl } : r)
      );

      console.log('Avatar uploaded successfully!');
    } catch (err) {
      console.error('Error uploading avatar:', err);
      alert(`Failed to upload avatar: ${err.message || 'Unknown error'}`);
    } finally {
      setUploadingAvatar(null);
    }
  };

  const handleSave = () => {
    onUpdate({ ...styles, reviews });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-yellow-100 rounded-xl">
              <SafeIcon icon={FiSettings} className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-lg uppercase tracking-tight">Reviews Settings</h3>
              <p className="text-xs text-gray-500 font-medium">Manage reviews and styling</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left Column - Reviews Management */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reviews ({reviews.length})</h4>
                <button
                  onClick={handleAddReview}
                  className="px-3 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 rounded-lg text-xs font-bold flex items-center space-x-1 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-3 h-3" />
                  <span>Add Review</span>
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-400">Loading reviews...</div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  <AnimatePresence>
                    {reviews.map((review) => (
                      <motion.div
                        key={review.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className={`border rounded-xl p-4 transition-all ${editingReview?.id === review.id ? 'border-yellow-400 bg-yellow-50/50' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        {editingReview?.id === review.id ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={review.name}
                              onChange={(e) => handleUpdateReview(review.id, { name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold focus:outline-none focus:border-yellow-400"
                              placeholder="Reviewer Name"
                            />
                            <input
                              type="text"
                              value={review.role}
                              onChange={(e) => handleUpdateReview(review.id, { role: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-yellow-400"
                              placeholder="Role/Title"
                            />
                            <textarea
                              value={review.content}
                              onChange={(e) => handleUpdateReview(review.id, { content: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 resize-none"
                              rows="3"
                              placeholder="Review content..."
                            />
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-1">
                                {[1, 2, 3, 4, 5].map(star => (
                                  <button
                                    key={star}
                                    onClick={() => handleUpdateReview(review.id, { rating: star })}
                                    className="focus:outline-none"
                                  >
                                    <SafeIcon
                                      icon={FiStar}
                                      className={`w-5 h-5 transition-colors ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                    />
                                  </button>
                                ))}
                              </div>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleAvatarUpload(review.id, file);
                                    }
                                    // Reset input value so consecutive uploads of the same file trigger onChange
                                    e.target.value = '';
                                  }}
                                  disabled={uploadingAvatar === review.id}
                                />
                                <div className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center space-x-1 transition-colors ${
                                  uploadingAvatar === review.id 
                                    ? 'bg-yellow-100 text-yellow-700 cursor-not-allowed' 
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 cursor-pointer'
                                }`}>
                                  <SafeIcon 
                                    icon={uploadingAvatar === review.id ? FiSettings : FiUpload} 
                                    className={`w-3 h-3 ${uploadingAvatar === review.id ? 'animate-spin' : ''}`} 
                                  />
                                  <span>{uploadingAvatar === review.id ? 'Uploading...' : 'Upload Avatar'}</span>
                                </div>
                              </label>
                            </div>
                            {review.avatar_url && (
                              <div className="flex items-center space-x-2">
                                <img 
                                  src={review.avatar_url} 
                                  alt={review.name} 
                                  className="w-12 h-12 rounded-full object-cover border-2 border-yellow-400" 
                                />
                                <span className="text-xs text-green-600 font-medium">✓ Avatar uploaded</span>
                              </div>
                            )}
                            <div className="flex space-x-2 pt-2">
                              <button
                                onClick={() => setEditingReview(null)}
                                className="flex-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-bold transition-colors"
                              >
                                Done
                              </button>
                              <button
                                onClick={() => handleDeleteReview(review.id)}
                                className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold transition-colors"
                              >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-2">
                                {review.avatar_url && (
                                  <img src={review.avatar_url} alt={review.name} className="w-8 h-8 rounded-full object-cover" />
                                )}
                                <div>
                                  <div className="font-bold text-sm text-gray-900">{review.name}</div>
                                  {review.role && <div className="text-xs text-gray-500">{review.role}</div>}
                                </div>
                              </div>
                              <button
                                onClick={() => setEditingReview(review)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                <SafeIcon icon={FiEdit2} className="w-4 h-4 text-gray-400" />
                              </button>
                            </div>
                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map(star => (
                                <SafeIcon
                                  key={star}
                                  icon={FiStar}
                                  className={`w-3 h-3 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                            <p className="text-xs text-gray-600 line-clamp-2">{review.content}</p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Right Column - Style Settings */}
            <div className="space-y-6">
              {/* Layout Options */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center space-x-2">
                  <SafeIcon icon={FiLayout} className="w-3 h-3" />
                  <span>Layout Columns</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map(col => (
                    <button
                      key={col}
                      onClick={() => handleChange('columns', col)}
                      className={`py-2 rounded-xl text-xs font-bold border-2 transition-all ${styles.columns === col ? 'border-yellow-400 bg-yellow-50 text-yellow-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}
                    >
                      {col} Column{col > 1 ? 's' : ''}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center space-x-2">
                  <SafeIcon icon={FiImage} className="w-3 h-3" />
                  <span>Display Options</span>
                </label>
                <div className="space-y-2">
                  <label className="flex items-center justify-between p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                    <span className="text-sm font-bold text-gray-700">Show Reviewer Avatars</span>
                    <input type="checkbox" checked={styles.showAvatars} onChange={(e) => handleChange('showAvatars', e.target.checked)} className="w-4 h-4 text-yellow-500 rounded focus:ring-yellow-500" />
                  </label>
                  <label className="flex items-center justify-between p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                    <span className="text-sm font-bold text-gray-700">Show Overall Summary</span>
                    <input type="checkbox" checked={styles.showSummary} onChange={(e) => handleChange('showSummary', e.target.checked)} className="w-4 h-4 text-yellow-500 rounded focus:ring-yellow-500" />
                  </label>
                </div>
              </div>

              {/* Colors */}
              <div className="space-y-4">
                <ColorPickerMock label="Card Background" color={styles.cardBgColor} onChange={(c) => handleChange('cardBgColor', c)} />
                <ColorPickerMock label="Text Color" color={styles.textColor} onChange={(c) => handleChange('textColor', c)} />
                <ColorPickerMock label="Star Color" color={styles.starColor} onChange={(c) => handleChange('starColor', c)} />
              </div>

              {/* Border Radius */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Card Border Radius</label>
                  <span className="text-xs font-bold text-gray-500">{styles.borderRadius}px</span>
                </div>
                <input type="range" min="0" max="40" value={styles.borderRadius} onChange={(e) => handleChange('borderRadius', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500" />
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-gray-100 bg-gray-50">
          <button onClick={handleSave} className="w-full py-3.5 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-lg flex items-center justify-center space-x-2">
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            <span>Save All Changes</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ReviewsWidgetPopup;